(() => {
  'use strict';

  const looxCloseSelector = 'a.loox-pn-close';
  const looxCloseLabel = 'Fechar notificação de avaliações';
  const looxReviewImageSelector = '.loox-pn-review__image > img';
  const looxReviewImageAlt = 'Foto enviada por cliente em uma avaliação';
  const klaviyoRootSelector = '[class*="klaviyo-form"], [data-testid*="klaviyo"]';
  const klaviyoCloseSelector = 'button[aria-label="Close dialog"], button.klaviyo-close-form';
  const klaviyoTargetSelector = [
    klaviyoCloseSelector,
    'button.needsclick[class*="kl-private-reset-css"]',
    'input.needsclick[class*="kl-private-reset-css"]:not([type="hidden"]):not(.klaviyo-sr-only)',
    'a[data-av-klaviyo-privacy="current"]'
  ].join(', ');
  const smileLauncherSelector = '#smile-ui-lite-launcher-frame-container';
  const integrationSelector = [
    looxCloseSelector,
    looxReviewImageSelector,
    klaviyoRootSelector,
    klaviyoTargetSelector,
    smileLauncherSelector
  ].join(', ');
  const legacyPrivacyHosts = new Set(['australiavibes.com.br', 'www.australiavibes.com.br']);
  const privacyPolicyLabel = document.documentElement.dataset.avPrivacyLabel || '';
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const smileMotionObservers = new WeakMap();
  const queuedRoots = new Set();
  let queued = false;

  const hasAccessibleName = (control) => (
    control.hasAttribute('aria-label')
    || control.hasAttribute('aria-labelledby')
    || control.textContent.trim().length > 0
  );

  const nameLooxCloseControls = (root) => {
    const controls = [];
    if (root instanceof Element && root.matches(looxCloseSelector)) controls.push(root);
    if (root.querySelectorAll) controls.push(...root.querySelectorAll(looxCloseSelector));

    controls.forEach((control) => {
      if (!hasAccessibleName(control)) control.setAttribute('aria-label', looxCloseLabel);
      control.style.setProperty('min-inline-size', '44px');
      control.style.setProperty('min-block-size', '44px');
      control.dataset.avLooxCloseTarget = '44';
    });
  };

  const nameLooxReviewImages = (root) => {
    const images = [];
    if (root instanceof Element && root.matches(looxReviewImageSelector)) images.push(root);
    if (root.querySelectorAll) images.push(...root.querySelectorAll(looxReviewImageSelector));

    images.forEach((image) => {
      if (!image.hasAttribute('alt') || image.getAttribute('alt').trim() === '') {
        image.setAttribute('alt', looxReviewImageAlt);
      }
      image.dataset.avLooxReviewAlt = 'customer-photo';
    });
  };

  const replaceLegacyUrlInTextNodes = (anchor, replacementLabel) => {
    if (!replacementLabel) return;
    const walker = document.createTreeWalker(anchor, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      node.nodeValue = node.nodeValue.replace(
        /(?:https?:\/\/)?(?:www\.)?australiavibes\.com\.br(?:\/policies\/privacy-policy)?\/?/giu,
        replacementLabel
      );
    });
  };

  const repairKlaviyoRoot = (root) => {
    root.querySelectorAll(klaviyoCloseSelector).forEach((control) => {
      control.setAttribute('aria-label', 'Fechar formulário');
      control.dataset.avKlaviyoCloseTarget = '56';
    });

    root.querySelectorAll('h1').forEach((heading) => {
      const replacement = document.createElement('h2');
      [...heading.attributes].forEach((attribute) => replacement.setAttribute(attribute.name, attribute.value));
      replacement.innerHTML = heading.innerHTML;
      replacement.dataset.avKlaviyoHeading = 'h2';
      heading.replaceWith(replacement);
    });

    root.querySelectorAll('a[href]').forEach((anchor) => {
      let parsed;
      try {
        parsed = new URL(anchor.href, document.baseURI);
      } catch (_) {
        return;
      }
      if (!legacyPrivacyHosts.has(parsed.hostname.toLowerCase()) || parsed.pathname !== '/policies/privacy-policy') return;

      const canonicalUrl = new URL('/policies/privacy-policy', document.baseURI).href;
      anchor.href = canonicalUrl;
      replaceLegacyUrlInTextNodes(anchor, privacyPolicyLabel);
      if (anchor.getAttribute('aria-label')?.includes('australiavibes.com.br')) {
        anchor.setAttribute('aria-label', privacyPolicyLabel || anchor.getAttribute('aria-label').replace(/(?:www\.)?australiavibes\.com\.br/giu, new URL(canonicalUrl).hostname));
      }
      anchor.dataset.avKlaviyoPrivacy = 'current';
    });
  };

  const repairKlaviyoTargets = (root) => {
    const controls = [];
    if (root instanceof Element && root.matches(klaviyoTargetSelector)) controls.push(root);
    if (root.querySelectorAll) controls.push(...root.querySelectorAll(klaviyoTargetSelector));

    controls.forEach((control) => {
      control.style.setProperty('min-block-size', '56px', 'important');
      if (control.matches(klaviyoCloseSelector)) {
        control.setAttribute('aria-label', 'Fechar formulário');
        control.style.setProperty('min-inline-size', '56px', 'important');
        control.dataset.avKlaviyoCloseTarget = '56';
      }
      if (control.matches('a[href]')) {
        control.style.setProperty('display', 'inline-flex', 'important');
        control.style.setProperty('align-items', 'center', 'important');
      }
      control.dataset.avKlaviyoScaledTarget = '56';
    });
  };

  const repairKlaviyo = (root) => {
    repairKlaviyoTargets(root);
    const roots = new Set();
    if (root instanceof Element) {
      if (root.matches(klaviyoRootSelector)) roots.add(root);
      const closest = root.closest(klaviyoRootSelector);
      if (closest) roots.add(closest);
    }
    if (root.querySelectorAll) root.querySelectorAll(klaviyoRootSelector).forEach((node) => roots.add(node));
    roots.forEach(repairKlaviyoRoot);
    repairKlaviyoTargets(root);
  };

  const observeSmileMotionStyle = (launcher) => {
    if (smileMotionObservers.has(launcher)) return;
    const observer = new MutationObserver((mutations) => {
      if (
        !reducedMotionQuery.matches
        || !mutations.some((mutation) => mutation.type === 'attributes' && mutation.attributeName === 'style')
      ) return;
      const animationGuarded = launcher.style.getPropertyValue('animation-name') === 'none'
        && launcher.style.getPropertyPriority('animation-name') === 'important';
      const transitionGuarded = launcher.style.getPropertyValue('transition-property') === 'none'
        && launcher.style.getPropertyPriority('transition-property') === 'important';
      if (!animationGuarded || !transitionGuarded) repairSmileReducedMotion(launcher);
    });
    observer.observe(launcher, { attributes: true, attributeFilter: ['style'] });
    smileMotionObservers.set(launcher, observer);
  };

  const repairSmileReducedMotion = (root) => {
    const launchers = [];
    if (root instanceof Element && root.matches(smileLauncherSelector)) launchers.push(root);
    if (root.querySelectorAll) launchers.push(...root.querySelectorAll(smileLauncherSelector));

    launchers.forEach((launcher) => {
      observeSmileMotionStyle(launcher);
      if (reducedMotionQuery.matches) {
        if (!launcher.hasAttribute('data-av-smile-motion-guard')) {
          launcher.dataset.avSmileOriginalAnimation = launcher.style.getPropertyValue('animation');
          launcher.dataset.avSmileOriginalAnimationPriority = launcher.style.getPropertyPriority('animation');
          launcher.dataset.avSmileOriginalTransition = launcher.style.getPropertyValue('transition');
          launcher.dataset.avSmileOriginalTransitionPriority = launcher.style.getPropertyPriority('transition');
          launcher.dataset.avSmileMotionGuard = 'reduce';
        }
        launcher.getAnimations?.().forEach((animation) => animation.cancel());
        launcher.style.setProperty('animation', 'none', 'important');
        launcher.style.setProperty('transition', 'none', 'important');
        launcher.getAnimations?.().forEach((animation) => animation.cancel());
        return;
      }

      if (!launcher.hasAttribute('data-av-smile-motion-guard')) return;
      const restore = (property, value, priority) => {
        if (value) launcher.style.setProperty(property, value, priority);
        else launcher.style.removeProperty(property);
      };
      restore('animation', launcher.dataset.avSmileOriginalAnimation || '', launcher.dataset.avSmileOriginalAnimationPriority || '');
      restore('transition', launcher.dataset.avSmileOriginalTransition || '', launcher.dataset.avSmileOriginalTransitionPriority || '');
      delete launcher.dataset.avSmileOriginalAnimation;
      delete launcher.dataset.avSmileOriginalAnimationPriority;
      delete launcher.dataset.avSmileOriginalTransition;
      delete launcher.dataset.avSmileOriginalTransitionPriority;
      delete launcher.dataset.avSmileMotionGuard;
    });
  };

  const stopLateSmileAnimation = (event) => {
    const launcher = event.target;
    if (
      !reducedMotionQuery.matches
      || !(launcher instanceof Element)
      || !launcher.matches(smileLauncherSelector)
    ) return;
    repairSmileReducedMotion(launcher);
  };

  const repairSmileMotionPreference = () => {
    repairSmileReducedMotion(document);
    if (!reducedMotionQuery.matches) return;
    requestAnimationFrame(() => {
      if (reducedMotionQuery.matches) repairSmileReducedMotion(document);
    });
  };

  const repairIntegrations = (root = document) => {
    nameLooxCloseControls(root);
    nameLooxReviewImages(root);
    repairKlaviyo(root);
    repairSmileReducedMotion(root);
  };

  const mayContainIntegration = (root) => (
    root.matches(integrationSelector)
    || Boolean(root.closest(klaviyoRootSelector))
    || Boolean(root.querySelector(integrationSelector))
  );

  const scheduleRepair = (mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((candidate) => {
        const root = candidate instanceof Element ? candidate : candidate.parentElement;
        if (root && mayContainIntegration(root)) queuedRoots.add(root);
      });
    });
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      const roots = [...queuedRoots].filter((root) => root.isConnected);
      queuedRoots.clear();
      roots
        .filter((root) => !roots.some((candidate) => candidate !== root && candidate.contains(root)))
        .forEach(repairIntegrations);
    });
  };

  const start = () => {
    repairIntegrations(document);
    const observer = new MutationObserver(scheduleRepair);
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('animationstart', stopLateSmileAnimation, true);
    reducedMotionQuery.addEventListener?.('change', repairSmileMotionPreference);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
